/**
 * Real-time Updates Hook
 * Subscribes to WebSocket push updates from the server
 * Integrates with Zustand stores to maintain global state
 * Updates persist even when components unmount
 */

import { useEffect, useRef } from 'react';
import { useCognito } from '../contexts/CognitoProvider';
import { fetchCDDRecords } from '../services/configService';
import { eventCentral } from '../services/eventCentral';
import socketService from '../services/socketService';
import useCddRecordsStore from '../stores/cddRecordsStore';
import useInvitationsStore from '../stores/invitationsStore';

export interface RealtimeUpdate {
  messageType: 'CDDRecordUpdated' | 'InvitationUpdated' | 'DashboardUpdated' | 'RecordStatusChanged';
  timestamp: string;
  data: any;
}

export interface RealtimeCallbacks {
  onCDDRecordUpdated?: (data: any) => void;
  onInvitationUpdated?: (data: any) => void;
  onInvitationCreated?: (data: any) => void;
  onDashboardUpdated?: (data: any) => void;
  onRecordStatusChanged?: (data: any) => void;
  onError?: (error: Error) => void;
}

export const unwrapRealtimePayload = (payload: any, messageType: string): any => {
  let current = payload;
  for (let depth = 0; depth < 4 && current && typeof current === 'object'; depth += 1) {
    const nested = current[messageType] ?? current.data;
    if (!nested || nested === current) break;
    current = nested;
  }
  return current ?? {};
};

export const getRealtimeRecordId = (payload: any): string | undefined => {
  const id = payload?.id ?? payload?.caseId ?? payload?.referenceNumber;
  return id == null || id === '' ? undefined : String(id);
};

/**
 * Hook to subscribe to real-time updates from WebSocket
 * Updates are persisted in Zustand stores
 * Automatically unsubscribes on unmount
 */
export const useRealtimeUpdates = (callbacks?: RealtimeCallbacks) => {
  const { isAuthenticated } = useCognito();
  const callbacksRef = useRef(callbacks);

  // Get store actions
  const handleStoreRecordStatusChanged = useCddRecordsStore((state) => state.handleRecordStatusChanged);
  const handleRecordUpdated = useCddRecordsStore((state) => state.handleRecordUpdated);
  const updateInvitation = useInvitationsStore((state) => state.updateInvitation);
  const addInvitation = useInvitationsStore((state) => state.addInvitation);

  // Update ref when callbacks change
  useEffect(() => {
    callbacksRef.current = callbacks;
  }, [callbacks]);

  // Subscribe to real-time updates
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }


    // Handler for CDD Record updates
    const handleCDDRecordUpdated = (data: any) => {
      const payload = unwrapRealtimePayload(data, 'CDDRecordUpdated');
      const id = getRealtimeRecordId(payload);
      const update = id ? { ...payload, id } : payload;
      if (id) handleRecordUpdated(update);
      callbacksRef.current?.onCDDRecordUpdated?.(update);
    };

    // Handler for Invitation updates
    const handleInvitationUpdated = (data: any) => {
      updateInvitation(data.id, data);
      callbacksRef.current?.onInvitationUpdated?.(data);
    };

    // Handler for new invitations pushed by the server
    const handleInvitationCreated = (data: any) => {
      if (data?.id) addInvitation(data);
      callbacksRef.current?.onInvitationCreated?.(data);
    };

    // Handler for Dashboard updates — delegates side-effects to caller
    const handleDashboardUpdated = (data: any) => {
      callbacksRef.current?.onDashboardUpdated?.(data);
    };

    // Handler for Record status changes
    const handleRecordStatusChanged = (data: any) => {
      const payload = unwrapRealtimePayload(data, 'RecordStatusChanged');
      const id = getRealtimeRecordId(payload);
      const status = payload.status ?? payload.newStatus ?? payload.reportStatus ?? payload.stage;
      const update = id ? { ...payload, id } : payload;
      if (id && status != null) {
        handleStoreRecordStatusChanged(
          id,
          status,
          payload.previousStatus ?? 'unknown',
          update,
        );
      }
      callbacksRef.current?.onRecordStatusChanged?.(update);
    };

    socketService.on('CDDRecordUpdated', handleCDDRecordUpdated);
    socketService.on('InvitationUpdated', handleInvitationUpdated);
    socketService.on('InvitationCreated', handleInvitationCreated);
    socketService.on('DashboardUpdated', handleDashboardUpdated);
    socketService.on('RecordStatusChanged', handleRecordStatusChanged);

    return () => {
      socketService.off('CDDRecordUpdated', handleCDDRecordUpdated);
      socketService.off('InvitationUpdated', handleInvitationUpdated);
      socketService.off('InvitationCreated', handleInvitationCreated);
      socketService.off('DashboardUpdated', handleDashboardUpdated);
      socketService.off('RecordStatusChanged', handleRecordStatusChanged);
    };
  }, [isAuthenticated, updateInvitation, addInvitation, handleRecordUpdated, handleStoreRecordStatusChanged]);

  // NotifyOnboardingStageEvent fires when a C2 progresses their journey (case status changed)
  useEffect(() => {
    if (!isAuthenticated) return;
    eventCentral.registerHandlers({
      onOnboardingStageUpdate: async () => {
        try {
          const result = await fetchCDDRecords();
          useCddRecordsStore.getState().setRecords(result.data);
        } catch { }
      },
    });
  }, [isAuthenticated]);
};

export default useRealtimeUpdates;
